import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  FaCheckCircle,
  FaCloudUploadAlt,
  FaDownload,
  FaExclamationTriangle,
  FaFileExcel,
  FaFileImport,
  FaTimesCircle,
  FaUsers,
} from "react-icons/fa";
import { collection, doc, getDocs, getFirestore, query, where, writeBatch } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import * as XLSX from "xlsx";

const db = getFirestore(firebaseApp);

const clean = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value).trim();
  if (value instanceof Date) return value;
  return value ?? "";
};

const toUpperText = (value) => String(clean(value) || "").toUpperCase();

const validateDNI = (dni) => /^\d{8,9}$/.test(String(clean(dni) || ""));

const validateEmail = (email) => {
  if (!String(clean(email) || "")) return true;
  const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return emailPattern.test(String(clean(email)));
};

const parseDecimal = (value) => {
  const raw = String(clean(value) || "").replace(/\s/g, "");
  if (!raw) return 0;
  const normalized = raw.includes(",") && raw.includes(".")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw.replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseDateValue = (value) => {
  if (!value && value !== 0) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  if (typeof value === "number" && value > 10000 && value < 90000) {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    return new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
  }

  const source = String(clean(value));
  if (!source) return null;

  const isoMatch = source.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  const latamMatch = source.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (latamMatch) {
    const [, d, m, y] = latamMatch;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  const parsed = new Date(source);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  return null;
};

const buildEmpleadoPayload = ({ row, rucEmpresa, exists }) => {
  const [
    dniRaw,
    nombresRaw,
    apellidosRaw,
    fechaNacimientoRaw,
    fechaIngresoRaw,
    sueldoRaw,
    cargoRaw,
    areaRaw,
    emailRaw,
    telefonoRaw,
    domicilioRaw,
    regimenRaw,
    afpRaw,
    cusppRaw,
    centroCostoRaw,
  ] = row;

  const now = new Date();
  const dni = String(clean(dniRaw));
  const payload = {
    tipo_documento: "DNI",
    dni,
    nombres: toUpperText(nombresRaw),
    apellidos: toUpperText(apellidosRaw),
    sexo: "",
    fecha_nacimiento: parseDateValue(fechaNacimientoRaw),
    domicilio: toUpperText(domicilioRaw),
    nacionalidad: "",
    fecha_ingreso: parseDateValue(fechaIngresoRaw) || now,
    fecha_reingreso: null,
    fecha_cese: null,
    cargo: toUpperText(cargoRaw),
    ocupacion_cod: "251101",
    pais_emisor_cod: "604",
    nacionalidad_cod: "604",
    regimen_laboral_cod: "01",
    categoria_trabajador_cod: "02",
    nivel_educativo_cod: "05",
    discapacidad_cod: "0",
    sctr_salud_cod: "0",
    sctr_pension_cod: "0",
    tipo_contrato_cod: "01",
    regimen_jornada_laboral_cod: "1",
    sujeto_fiscalizacion_horario_cod: "1",
    horario_nocturno_cod: "0",
    sindicalizado_cod: "0",
    periodicidad_ingresos_cod: "1",
    percibe_renta5ta_cod: "1",
    ocupacion_publica_cod: "0",
    categoria_periodo_per_cod: "01",
    regimen_pensionario: toUpperText(regimenRaw) || "ONP",
    afp: toUpperText(afpRaw),
    cuspp: String(clean(cusppRaw) || ""),
    fecha_termino_comision_afp: null,
    sueldo_base: parseDecimal(sueldoRaw),
    asignacion_familiar: false,
    centro_costo: toUpperText(centroCostoRaw) || "ADMINISTRATIVO",
    categoria: "",
    area: toUpperText(areaRaw),
    cuenta_bancaria: "",
    banco: "",
    email: String(clean(emailRaw) || "").toLowerCase(),
    telefono: String(clean(telefonoRaw) || ""),
    essalud_vida_importe: 0,
    vida_ley_importe: 0,
    sctr: "",
    activo: true,
    situacion: "ACTIVO",
    ruc_empresa: rucEmpresa,
    updated_at: now,
  };

  if (!exists) payload.created_at = now;
  return payload;
};

function EmpleadosMasivos({ usuario }) {
  const rucEmpresa = usuario?.ruc || "";

  const [selectedFile, setSelectedFile] = useState(null);
  const [tempData, setTempData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isDataValid, setIsDataValid] = useState(false);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [filterType, setFilterType] = useState("all");

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setResults([]);
    setValidationErrors([]);
    setIsDataValid(false);
    setTempData([]);

    if (file) preValidateFile(file);
  };

  const preValidateFile = (file) => {
    const fileReader = new FileReader();
    fileReader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: 1, defval: "" });

        const errors = [];
        const seenDni = new Set();

        data.forEach((row, index) => {
          if (!row || row.length === 0 || row.every((cell) => String(clean(cell)) === "")) return;

          const rowNum = index + 2;
          const dni = String(clean(row[0]));
          const nombres = String(clean(row[1]));
          const apellidos = String(clean(row[2]));
          const email = String(clean(row[8] || ""));

          if (!dni) errors.push(`Fila ${rowNum}: DNI es obligatorio`);
          else if (!validateDNI(dni)) errors.push(`Fila ${rowNum}: DNI inválido (${dni})`);
          else if (seenDni.has(dni)) errors.push(`Fila ${rowNum}: DNI duplicado en archivo (${dni})`);

          if (!nombres) errors.push(`Fila ${rowNum}: Nombres son obligatorios`);
          if (!apellidos) errors.push(`Fila ${rowNum}: Apellidos son obligatorios`);
          if (email && !validateEmail(email)) errors.push(`Fila ${rowNum}: Email inválido (${email})`);

          seenDni.add(dni);
        });

        setValidationErrors(errors);
        setTempData(data);
        setIsDataValid(errors.length === 0 && data.length > 0);
      } catch (error) {
        console.error("Error validando Excel de empleados:", error);
        setValidationErrors(["No se pudo leer el archivo Excel"]);
        setIsDataValid(false);
      }
    };
    fileReader.readAsBinaryString(file);
  };

  const descargarPlantillaExcel = () => {
    const headers = [
      "DNI",
      "Nombres",
      "Apellidos",
      "Fecha Nacimiento",
      "Fecha Ingreso",
      "Sueldo Base",
      "Cargo",
      "Area",
      "Email",
      "Telefono",
      "Domicilio",
      "Regimen Pensionario",
      "AFP",
      "CUSPP",
      "Centro Costo",
    ];

    const ejemplo = [
      [
        "12345678",
        "JUAN CARLOS",
        "PEREZ GONZALES",
        "15/06/1994",
        "01/03/2026",
        "1800",
        "ANALISTA",
        "SISTEMAS",
        "juan.perez@empresa.com",
        "987654321",
        "LIMA",
        "ONP",
        "",
        "",
        "ADMINISTRATIVO",
      ],
    ];

    const instrucciones = [
      ["INSTRUCCIONES - CARGA MASIVA DE EMPLEADOS"],
      [""],
      ["Obligatorios: DNI, Nombres, Apellidos"],
      ["DNI debe tener 8 o 9 dígitos"],
      ["Fechas aceptadas: DD/MM/YYYY o YYYY-MM-DD"],
      ["Si no indicas Fecha Ingreso, se usará la fecha actual"],
      ["Regimen Pensionario recomendado: ONP o AFP"],
      [""],
      ["Puedes editar esta plantilla y subirla en la primera hoja"],
    ];

    const wb = XLSX.utils.book_new();
    const wsData = XLSX.utils.aoa_to_sheet([headers, ...ejemplo]);
    wsData["!cols"] = [
      { wch: 12 },
      { wch: 18 },
      { wch: 22 },
      { wch: 16 },
      { wch: 16 },
      { wch: 12 },
      { wch: 18 },
      { wch: 16 },
      { wch: 28 },
      { wch: 14 },
      { wch: 26 },
      { wch: 18 },
      { wch: 12 },
      { wch: 16 },
      { wch: 18 },
    ];

    const wsInstructions = XLSX.utils.aoa_to_sheet(instrucciones);
    wsInstructions["!cols"] = [{ wch: 80 }];

    XLSX.utils.book_append_sheet(wb, wsData, "Plantilla Empleados");
    XLSX.utils.book_append_sheet(wb, wsInstructions, "Instrucciones");

    const fecha = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `plantilla-empleados-${fecha}.xlsx`);
  };

  const procesarExcel = async () => {
    if (!selectedFile || !isDataValid || !rucEmpresa) return;

    setLoading(true);
    setStatusMessage("Preparando importación masiva...");

    try {
      const empleadosQ = query(collection(db, "pl_empleados"), where("ruc_empresa", "==", rucEmpresa));
      const empleadosSnap = await getDocs(empleadosQ);
      const existentes = new Set();
      empleadosSnap.forEach((docRef) => existentes.add(String(clean(docRef.data()?.dni || docRef.id))));

      const validRows = [];
      const newResults = [];

      tempData.forEach((row) => {
        if (!row || row.length === 0 || row.every((cell) => String(clean(cell)) === "")) return;

        const dni = String(clean(row[0]));
        const nombres = toUpperText(row[1]);
        const apellidos = toUpperText(row[2]);
        const email = String(clean(row[8] || ""));

        if (!dni || !validateDNI(dni)) {
          newResults.push({ dni, nombres, apellidos, cargo: "", status: "DNI inválido", success: false });
          return;
        }
        if (!nombres || !apellidos) {
          newResults.push({ dni, nombres, apellidos, cargo: "", status: "Faltan nombres/apellidos", success: false });
          return;
        }
        if (email && !validateEmail(email)) {
          newResults.push({ dni, nombres, apellidos, cargo: "", status: "Email inválido", success: false });
          return;
        }

        validRows.push(row);
      });

      if (!validRows.length) {
        setResults(newResults);
        return;
      }

      const BATCH_SIZE = 300;
      for (let i = 0; i < validRows.length; i += BATCH_SIZE) {
        const chunk = validRows.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);

        chunk.forEach((row) => {
          const dni = String(clean(row[0]));
          const exists = existentes.has(dni);
          const payload = buildEmpleadoPayload({ row, rucEmpresa, exists });

          batch.set(doc(db, "pl_empleados", dni), payload, { merge: true });
          newResults.push({
            dni,
            nombres: payload.nombres,
            apellidos: payload.apellidos,
            cargo: payload.cargo,
            status: exists ? "Actualizado" : "Creado",
            success: true,
          });
        });

        await batch.commit();
        const currentProgress = Math.round(((i + chunk.length) / validRows.length) * 100);
        setProgress(Math.min(currentProgress, 100));
        setStatusMessage(`Procesando registros ${i + chunk.length} de ${validRows.length}...`);
      }

      setResults(newResults);
    } catch (error) {
      console.error("Error cargando empleados masivos desde Excel:", error);
      setResults((prev) => [
        ...prev,
        {
          dni: "-",
          nombres: "-",
          apellidos: "-",
          cargo: "-",
          status: "Error procesando archivo Excel",
          success: false,
        },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setProgress(0);
        setStatusMessage("");
      }, 1200);
    }
  };

  const exportErrorsToExcel = () => {
    const errorRows = results.filter((item) => !item.success);
    if (!errorRows.length) return;

    const wsData = [
      ["DNI", "Nombres", "Apellidos", "Cargo", "Estado"],
      ...errorRows.map((row) => [row.dni, row.nombres, row.apellidos, row.cargo, row.status]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Errores Empleados");
    XLSX.writeFile(wb, `errores-empleados-${new Date().getTime()}.xlsx`);
  };

  const filteredResults = results.filter((item) => {
    if (filterType === "success") return item.success;
    if (filterType === "error") return !item.success;
    return true;
  });

  const estadisticas = {
    total: results.length,
    exitosos: results.filter((r) => r.success).length,
    errores: results.filter((r) => !r.success).length,
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
          <Card.Header
            className="text-white py-4"
            style={{ background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)", borderBottom: "3px solid #D2691E" }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaFileImport className="me-3" />
                Empleados Masivos
              </h2>
              <p className="mb-0 opacity-75">Genera y actualiza empleados desde usuarios o Excel</p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {loading && (
              <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75" style={{ zIndex: 1080 }}>
                <div className="bg-white p-4 rounded-3 shadow-lg" style={{ minWidth: "320px", maxWidth: "420px" }}>
                  <div className="text-center mb-3">
                    <Spinner animation="border" variant="warning" size="sm" className="me-2" />
                    <span className="fw-bold" style={{ color: "#8B4513" }}>Procesando empleados...</span>
                  </div>
                  <ProgressBar now={progress} label={`${progress}%`} variant="warning" animated className="mb-2" style={{ height: "20px" }} />
                  <p className="text-center text-muted small mb-0">{statusMessage}</p>
                </div>
              </div>
            )}

            <Row className="mb-4">
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{estadisticas.total}</h4>
                    <p className="mb-0">Total Procesados</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{estadisticas.exitosos}</h4>
                    <p className="mb-0">Exitosos</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{estadisticas.errores}</h4>
                    <p className="mb-0">Con Errores</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Alert variant="info" className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
              <div className="d-flex align-items-center gap-2">
                <FaExclamationTriangle className="text-info" />
                <div>
                  <strong>Flujo disponible:</strong>
                  <br />
                  <small>Descarga la plantilla de empleados, complétala y súbela para procesar la carga masiva.</small>
                </div>
              </div>
            </Alert>

            <Row className="g-4">
              <Col md={6}>
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", borderRadius: "15px 15px 0 0" }}>
                    <h5 className="mb-0 fw-semibold"><FaFileExcel className="me-2" />Plantilla Excel</h5>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <p className="text-muted mb-3">Descarga la plantilla, completa los datos y luego súbela para registrar empleados en lote.</p>
                    <Button
                      variant="success"
                      onClick={descargarPlantillaExcel}
                      className="w-100 d-flex align-items-center justify-content-center gap-2 py-3"
                      style={{ borderRadius: "10px" }}
                    >
                      <FaDownload /> Descargar Plantilla
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)", borderRadius: "15px 15px 0 0" }}>
                    <h5 className="mb-0 fw-semibold"><FaCloudUploadAlt className="me-2" />Subir Excel</h5>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Archivo .xlsx / .xls</Form.Label>
                      <Form.Control
                        type="file"
                        onChange={handleFileChange}
                        accept=".xlsx,.xls"
                        className="form-control-lg"
                        style={{ borderRadius: "10px", border: "2px dashed #dee2e6", padding: "16px" }}
                      />
                      <Form.Text className="text-muted">
                        {selectedFile ? `Archivo: ${selectedFile.name}` : "Selecciona un archivo Excel de empleados"}
                      </Form.Text>
                    </Form.Group>

                    {validationErrors.length > 0 && (
                      <Alert variant="warning" className="small py-2 mb-3">
                        <div className="fw-bold mb-1">Errores detectados:</div>
                        <ul className="mb-0 ps-3">
                          {validationErrors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                          {validationErrors.length > 5 && <li>Y {validationErrors.length - 5} más...</li>}
                        </ul>
                      </Alert>
                    )}

                    <Button
                      variant="warning"
                      onClick={procesarExcel}
                      disabled={!selectedFile || !isDataValid || loading || !rucEmpresa}
                      className="w-100 d-flex align-items-center justify-content-center gap-2 py-3 text-white"
                      style={{ borderRadius: "10px", background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)", border: "none" }}
                    >
                      <FaFileImport /> Procesar Excel
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {results.length > 0 && (
              <Card className="border-0 shadow-sm mt-4" style={{ borderRadius: "15px" }}>
                <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)", borderRadius: "15px 15px 0 0" }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-semibold"><FaUsers className="me-2" />Resultados</h5>
                    <div className="d-flex align-items-center gap-2">
                      {results.some((r) => !r.success) && (
                        <Button variant="outline-light" size="sm" onClick={exportErrorsToExcel}>
                          <FaDownload className="me-1" /> Errores
                        </Button>
                      )}
                      <div className="btn-group btn-group-sm bg-white rounded-pill p-1">
                        <Button variant={filterType === "all" ? "warning" : "outline-warning"} className="rounded-pill border-0" onClick={() => setFilterType("all")}>Todos</Button>
                        <Button variant={filterType === "success" ? "success" : "outline-success"} className="rounded-pill border-0" onClick={() => setFilterType("success")}>Exitosos</Button>
                        <Button variant={filterType === "error" ? "danger" : "outline-danger"} className="rounded-pill border-0" onClick={() => setFilterType("error")}>Errores</Button>
                      </div>
                    </div>
                  </div>
                </Card.Header>

                <Card.Body className="p-0">
                  <div style={{ maxHeight: "520px", overflowY: "auto" }}>
                    <Table hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
                      <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0, zIndex: 10 }}>
                        <tr>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: "5%" }}>#</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: "12%" }}>DNI</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: "18%" }}>Nombres</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: "20%" }}>Apellidos</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: "20%" }}>Cargo</th>
                          <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: "25%" }}>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredResults.map((row, index) => (
                          <tr key={`${row.dni}-${index}`} style={{ borderBottom: "1px solid #f1f3f4" }}>
                            <td className="text-center py-3 px-3"><Badge bg="dark">{index + 1}</Badge></td>
                            <td className="py-3 px-3 fw-semibold">{row.dni || "-"}</td>
                            <td className="py-3 px-3">{row.nombres || "-"}</td>
                            <td className="py-3 px-3">{row.apellidos || "-"}</td>
                            <td className="py-3 px-3">{row.cargo || "-"}</td>
                            <td className="text-center py-3 px-3">
                              <Badge bg={row.success ? "success" : "danger"} className="px-3 py-2">
                                {row.success ? <FaCheckCircle className="me-1" /> : <FaTimesCircle className="me-1" />}
                                {row.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default EmpleadosMasivos;
